import { Link } from 'react-router-dom';
import { LegalLayout } from './LegalLayout';
import { COMPANY, LEGAL_ROUTES } from './legal';

export function CookiePage() {
  return (
    <LegalLayout
      title="Cookie Policy"
      active="cookies"
      intro="How Revvly uses cookies and similar technologies on our website and web application at revvly.ai."
    >
      <p>
        This Cookie Policy explains how Revvly (&ldquo;Revvly&rdquo;, &ldquo;we&rdquo;,
        &ldquo;us&rdquo;, or &ldquo;our&rdquo;) uses cookies and similar technologies on our marketing
        website and web application (the &ldquo;Service&rdquo;). It should be read alongside our{' '}
        <Link to={LEGAL_ROUTES.privacy}>Privacy Policy</Link> and{' '}
        <Link to={LEGAL_ROUTES.terms}>Terms of Service</Link>.
      </p>
      <p>
        Revvly is operated by <strong>{COMPANY.legalName}</strong>, a company incorporated in{' '}
        {COMPANY.jurisdiction} ({COMPANY.companyNumber}), which is the data controller for Revvly. For
        questions about this policy, see <a href="#contact">Contact</a> below.
      </p>

      <h2>1. What are cookies?</h2>
      <p>
        Cookies are small text files placed on your device (computer, tablet, or phone) when you visit
        a website. They are widely used to make websites work, to make them work more efficiently, and
        to provide reporting information. We also use similar technologies such as{' '}
        <strong>local storage</strong>, <strong>pixels/tags</strong>, and{' '}
        <strong>software development kits (SDKs)</strong> — where this policy refers to
        &ldquo;cookies&rdquo;, it includes these technologies unless stated otherwise.
      </p>
      <p>Cookies may be:</p>
      <ul>
        <li>
          <strong>First-party</strong> — set by Revvly on the domain you are visiting.
        </li>
        <li>
          <strong>Third-party</strong> — set by another organization (for example, our payment
          processor or analytics provider).
        </li>
        <li>
          <strong>Session</strong> — deleted when you close your browser.
        </li>
        <li>
          <strong>Persistent</strong> — remain on your device until they expire or you delete them.
        </li>
      </ul>

      <h2>2. How we use cookies</h2>
      <p>We use cookies to:</p>
      <ul>
        <li>Keep you signed in and keep your account secure.</li>
        <li>Remember your preferences and settings.</li>
        <li>Process payments and subscriptions securely and detect fraud.</li>
        <li>Understand how the Service is used so we can improve it.</li>
        <li>
          Measure the performance of our marketing and, where you have consented, show you relevant
          advertising.
        </li>
      </ul>

      <h2>3. Categories of cookies we use</h2>
      <h3>3.1 Strictly necessary cookies</h3>
      <p>
        These cookies are essential for the Service to function and cannot be switched off in our
        systems. They are set in response to actions you take, such as signing in to the Revvly web
        application or setting your cookie preferences. Without them, the Service cannot be provided.
      </p>
      <div className="legal__table-wrap">
        <table className="legal__table">
          <thead>
            <tr>
              <th>Cookie</th>
              <th>Purpose</th>
              <th>Set by</th>
              <th>Duration</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code>revvly.session_token</code>
              </td>
              <td>Maintains your authenticated session</td>
              <td>Revvly (first-party)</td>
              <td>Session (up to 7 days)</td>
            </tr>
            <tr>
              <td>
                <code>revvly.session_data</code>
              </td>
              <td>Short-lived session cache to reduce repeated lookups</td>
              <td>Revvly (first-party)</td>
              <td>~5 minutes</td>
            </tr>
            <tr>
              <td>
                <code>revvly_cookie_consent</code>
              </td>
              <td>Stores your cookie-consent choices so we can honour them</td>
              <td>Revvly (first-party)</td>
              <td>~6 months</td>
            </tr>
            <tr>
              <td>
                <code>revvly_events_session</code>
              </td>
              <td>
                Holds a random session identifier (renewed after 30 minutes of inactivity) so that
                the pages you open in the Revvly app can be grouped into one visit. Local storage,
                not a cookie. See Section 4.1.1 of the Privacy Policy
              </td>
              <td>Revvly (first-party)</td>
              <td>Until 30 minutes of inactivity</td>
            </tr>
            <tr>
              <td>
                <code>revvly_events_optout</code>
              </td>
              <td>
                Remembers a choice to stop recording page use in the Revvly app, when one is made
              </td>
              <td>Revvly (first-party)</td>
              <td>Until cleared from your browser</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p>
        <strong>Consent:</strong> In most regions these do not require consent because they are
        necessary to deliver a service you have requested.
      </p>

      <h3>3.2 Analytics cookies</h3>
      <p>
        These cookies help us understand how visitors interact with our marketing site
        (www.revvly.ai) by collecting and reporting information pseudonymously. This helps us improve
        the Service. We load Google Analytics 4 through Google Tag Manager (container{' '}
        <code>GTM-PL4XXC77</code>).
      </p>
      <div className="legal__table-wrap">
        <table className="legal__table">
          <thead>
            <tr>
              <th>Cookie</th>
              <th>Purpose</th>
              <th>Set by</th>
              <th>Duration</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code>_ga</code>
              </td>
              <td>Distinguishes visitors (Google Analytics)</td>
              <td>Google (third-party)</td>
              <td>~2 years</td>
            </tr>
            <tr>
              <td>
                <code>_ga_PZ6KM253DJ</code>
              </td>
              <td>Persists Google Analytics 4 session state for our property (G-PZ6KM253DJ)</td>
              <td>Google (third-party)</td>
              <td>~2 years</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="legal__note">
        Google Tag Manager itself does not set analytics cookies; it loads the tags that do.
      </p>
      <p>
        <strong>Consent:</strong> These require your prior opt-in consent in the EEA, UK, and other
        regions with equivalent rules.
      </p>

      <h3>3.3 Payment cookies</h3>
      <p>
        We do not set payment cookies on our own site. Payments are handled by our payment processor,
        Stripe, on Stripe-hosted payment pages; Stripe may set its own cookies there (for example to
        prevent fraud) under Stripe&rsquo;s own policies. See <a href="#stripe">Section 4.1</a>.
      </p>

      <h3>3.4 Marketing and advertising cookies</h3>
      <p>
        These cookies and pixels are set on our marketing site to measure the effectiveness of our
        campaigns and, where permitted, to deliver and measure advertising. We currently deploy the
        Meta Pixel (ID <code>1534295808397386</code>).
      </p>
      <div className="legal__table-wrap">
        <table className="legal__table">
          <thead>
            <tr>
              <th>Cookie / pixel</th>
              <th>Purpose</th>
              <th>Set by</th>
              <th>Duration</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code>_fbp</code>
              </td>
              <td>Ad delivery and conversion measurement (Meta Pixel)</td>
              <td>Meta (third-party)</td>
              <td>~3 months</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p>
        <strong>Consent:</strong> These require your prior opt-in consent in the EEA, UK, and other
        regions with equivalent rules. In the United States, you may opt out of the &ldquo;sale&rdquo;
        or &ldquo;sharing&rdquo; of personal information associated with these cookies — see{' '}
        <a href="#us">Section 7.2</a>.
      </p>

      <h2>4. Third-party services</h2>
      <p>
        Some cookies are set by third parties that provide services on our behalf. These parties
        process data under their own privacy and cookie policies.
      </p>
      <h3 id="stripe">4.1 Stripe (payments and fraud prevention)</h3>
      <p>
        We use <strong>Stripe</strong> to process payments and subscriptions. Payment pages are hosted
        by Stripe, and Stripe may set its own cookies on those pages (for example to detect and prevent
        fraud). We do not set Stripe cookies on our own site. Stripe&rsquo;s cookies are governed by
        Stripe&rsquo;s Cookie Policy and Privacy Policy at{' '}
        <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">
          stripe.com
        </a>
        .
      </p>
      <h3>4.2 Google (analytics)</h3>
      <p>
        We use <strong>Google Analytics</strong> (via Google Tag Manager) to understand how our
        marketing site is used and to improve it. See Google&rsquo;s privacy documentation at{' '}
        <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
          policies.google.com
        </a>
        .
      </p>
      <h3>4.3 Meta (advertising)</h3>
      <p>
        We use the <strong>Meta Pixel</strong> to measure the effectiveness of our advertising. See
        Meta&rsquo;s privacy documentation at{' '}
        <a
          href="https://www.facebook.com/privacy/policy"
          target="_blank"
          rel="noopener noreferrer"
        >
          facebook.com
        </a>
        .
      </p>

      <h2>5. Legal basis and consent</h2>
      <p>
        Where required by law (including in the EEA under the ePrivacy Directive and GDPR, and in the
        UK under PECR and UK GDPR):
      </p>
      <ul>
        <li>
          We place <strong>strictly necessary</strong> cookies on the basis that they are essential to
          provide a service you have requested.
        </li>
        <li>
          We place all <strong>other cookies (analytics, marketing/advertising)</strong> only{' '}
          <strong>after you have given consent</strong> through our cookie banner.
        </li>
        <li>
          You can withdraw or change your consent at any time (see{' '}
          <a href="#choices">Section 6</a>). Withdrawing consent does not affect processing carried out
          before you withdrew it.
        </li>
      </ul>

      <h2 id="choices">6. Your choices and how to manage cookies</h2>
      <h3>Cookie preferences</h3>
      <p>
        You can accept or reject non-essential cookies, and change your choices at any time, using our
        cookie preferences tool available in the site footer.
      </p>
      <h3>Browser controls</h3>
      <p>
        Most browsers let you refuse or delete cookies through their settings. Details vary by browser
        — see the help pages for Chrome, Safari, Firefox, or Edge. Blocking strictly necessary cookies
        may stop parts of the Service from working.
      </p>
      <h3>Advertising opt-outs</h3>
      <p>You can opt out of interest-based advertising from many companies via:</p>
      <ul>
        <li>
          <strong>Your Online Choices</strong> (EU) — youronlinechoices.eu
        </li>
        <li>
          <strong>Digital Advertising Alliance</strong> (US) — optout.aboutads.info
        </li>
        <li>
          <strong>Network Advertising Initiative</strong> (US) — optout.networkadvertising.org
        </li>
      </ul>

      <h2>7. Region-specific information</h2>
      <h3>7.1 European Economic Area and United Kingdom</h3>
      <p>
        Non-essential cookies are set only with your prior consent, which we obtain through our cookie
        banner. You have the right to access, correct, or delete your personal data and to object to
        certain processing. See our <Link to={LEGAL_ROUTES.privacy}>Privacy Policy</Link> for how to
        exercise these rights.
      </p>
      <h3 id="us">7.2 United States</h3>
      <p>
        Depending on your state (for example, under the California Consumer Privacy Act, as amended by
        the CPRA), the use of certain advertising cookies may be considered a &ldquo;sale&rdquo; or
        &ldquo;sharing&rdquo; of personal information. You may opt out by adjusting your cookie
        preferences or by enabling the Global Privacy Control (GPC) signal in your browser.
      </p>

      <h2>8. Do Not Track</h2>
      <p>
        We honor browser opt-out signals. If your browser sends a &ldquo;Do Not Track&rdquo; (DNT)
        signal or a Global Privacy Control (GPC) signal, we treat it as a request not to set
        non-essential cookies: we do not load our analytics or marketing tags, and only strictly
        necessary cookies are used. You can still change this at any time through our{' '}
        <strong>Cookie settings</strong> control in the site footer, which records an explicit choice
        that takes precedence over the signal.
      </p>

      <h2>9. Changes to this policy</h2>
      <p>
        We may update this Cookie Policy from time to time to reflect changes in the cookies we use or
        for legal or operational reasons. We will post the updated version here and revise the
        &ldquo;Last updated&rdquo; date above. Where required, we will ask for your consent again.
      </p>

      <h2 id="contact">10. Contact us</h2>
      <p>If you have questions about this Cookie Policy or our use of cookies, contact us at:</p>
      <div className="legal__contact">
        <p>
          <strong>{COMPANY.legalName}</strong> — {COMPANY.companyNumber}
        </p>
        <p>
          Email: <a href={`mailto:${COMPANY.privacyEmail}`}>{COMPANY.privacyEmail}</a>
        </p>
      </div>
    </LegalLayout>
  );
}
